import { useEffect, useRef } from 'react';
import { usePollingStore } from '../lib/pollingStore';
import { templateSessionRequest } from '../lib/templateRequest';

const POLLING_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

/**
 * RecordsChecker - A polling component that monitors session status
 * 
 * POLLING PATTERN:
 * 1. Starts polling once on mount by calling performCheck()
 * 2. If response has 'retry-after' header, schedules next check after specified delay
 * 3. If no 'retry-after' header, polling completes with success
 * 4. Errors terminate polling and update store with error state
 * 5. Automatically stops polling after POLLING_TIMEOUT_MS to prevent infinite loops
 * 
 * TIMEOUT BEHAVIOR:
 * - Polling will automatically timeout after POLLING_TIMEOUT_MS
 * - When timeout occurs, sets error state and stops polling
 * - Timeout prevents resource waste from stuck/unresponsive sessions
 * 
 * IMPORTANT: Only one polling chain exists - subsequent calls are driven by
 * setTimeout with retry-after values, NOT by re-running useEffect
 * 
 * @param {string} accessToken - Authentication token for API requests
 * @param {string} sessionId - Unique session identifier to poll for
 */
export const RecordsChecker = ({ accessToken, sessionId }) => {
  const { setError, setSuccess, startPolling, stopPolling } = usePollingStore();
  const timeoutIdRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    // Try to start polling for this sessionId - will return false if already polling
    const shouldStartPolling = startPolling(sessionId);
    if (!shouldStartPolling) {
      return;
    }

    startTimeRef.current = Date.now();

    const performCheck = async () => {
      // Check if polling timeout has been reached
      if (Date.now() - startTimeRef.current > POLLING_TIMEOUT_MS) {
        console.log(`RecordsChecker: 10-minute timeout reached for sessionId ${sessionId} - stopping polling`);
        setError(new Error('Polling timeout: No response received within 10 minutes'), sessionId);
        return;
      }

      console.log(`RecordsChecker: performCheck() called for sessionId ${sessionId} - making request`);

      try {
        const res = await templateSessionRequest(accessToken, sessionId);
        const retryAfter = res.headers.get('retry-after');

        if (retryAfter === null) {
          // No retry-after header means session is ready - polling complete
          // TODO: need to check data in the session
          console.log(`RecordsChecker: No retry-after header for sessionId ${sessionId} - polling complete, setting success`);

          const jsonData = await res.json();
          setSuccess(jsonData.data, sessionId);
          return;
        }

        // Schedule next polling attempt using retry-after delay (convert seconds to ms)
        const delayMs = retryAfter * 1000;
        console.log(`RecordsChecker: Scheduling next check for sessionId ${sessionId} in ${retryAfter}s (${delayMs}ms)`);
        timeoutIdRef.current = setTimeout(performCheck, delayMs);

      } catch (error) {
        console.error(`RecordsChecker: Error occurred during request for sessionId ${sessionId}:`, error);
        setError(error, sessionId);
      }
    }

    // Start first check since we successfully claimed this sessionId for polling
    performCheck();

    // Cleanup: clear any pending timeout when component unmounts
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
      // Remove this sessionId from active polling when component unmounts
      stopPolling(sessionId);
    }

  }, [sessionId]); // Re-run if sessionId changes

  // The component is not rendered, it acts as a worker
  return null;
}
